import type { IKnowledgeBaseArticle } from './knowledge-base.catalog';

/**
 * Router links for knowledge-base articles, cached per article.
 *
 * `RouterLink` recomputes its href — which means serialising a `UrlTree` — whenever
 * its input changes *identity*. A template that calls a method to build the link
 * (`[routerLink]="articleLink(article)"`) hands it a brand-new array on every change
 * detection cycle, so the href is re-serialised for every link, every cycle. The KB
 * sidebar renders one link per article (~70 of them) on every KB page, which was
 * enough to saturate the main thread: the tab stopped responding to clicks entirely.
 *
 * The catalog entries are module-level singletons, so a WeakMap keyed on the article
 * hands every call site the same array instance and RouterLink stays quiet.
 */
const LINK_CACHE = new WeakMap<IKnowledgeBaseArticle, string[]>();

export function kbArticleLink(article: IKnowledgeBaseArticle): string[] {
  const cached = LINK_CACHE.get(article);
  if (cached) return cached;

  // `category_slug` is filled in by the catalog generator; fall back to the plain
  // article route rather than emitting an "undefined" segment if it is ever missing.
  const link = article.category_slug
    ? ['/knowledge-base', article.category_slug, article.slug]
    : ['/knowledge-base', article.slug];
  LINK_CACHE.set(article, link);
  return link;
}
